import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader, AlertCircle, Users, Plus, Trash2, X, History, Trophy } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { usernameCacheService } from '../../services/usernameCache';
import type { EventParticipant, EventParticipantRole } from '../../types/eventTeam';
import { EVENT_PARTICIPANT_ROLES } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { PlayerProfile } from '../PlayerProfile';
import { CreatorProfile } from '../CreatorProfile';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';
import { cn } from '../../utils/cn';
import { AwardPointsModal } from './AwardPointsModal';

const ROLE_META: Record<EventParticipantRole, { label: string; className: string }> = {
  participant: { label: 'Participant', className: 'bg-glass-200/10 text-glass-300 border-glass-200/20' },
  winner: { label: 'Winner', className: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25' },
  runner_up: { label: 'Runner-up', className: 'bg-gray-400/20 text-gray-200 border-gray-400/30' },
  helper: { label: 'Helper', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25' },
};

export interface EventParticipantsSectionProps {
  eventId: string;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

function ParticipantHistoryModal({ uuid, onClose }: { uuid: string; onClose: () => void }) {
  const [history, setHistory] = useState<Awaited<ReturnType<typeof eventTeamApiService.getParticipantHistory>> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    eventTeamApiService
      .getParticipantHistory(uuid)
      .then(setHistory)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load history'));
  }, [uuid]);

  return createPortal(
    <div className="modal-overlay z-[9999]" onClick={onClose}>
      <div
        className="modal-panel max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white inline-flex items-center gap-2">
            <History className="w-5 h-5 text-accent-400" />
            Participation history
          </h3>
          <button onClick={onClose} className="glass-button-secondary !p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mb-3">
          <PlayerProfile uuid={uuid} />
        </div>
        {error ? (
          <p className="text-red-300 text-sm">{error}</p>
        ) : history == null ? (
          <div className="flex items-center justify-center py-6">
            <Loader className="w-5 h-5 animate-spin text-accent-400" />
          </div>
        ) : history.length === 0 ? (
          <p className="text-glass-400 text-sm">This player hasn't participated in any recorded events.</p>
        ) : (
          <div className="space-y-1.5">
            {history.map((entry) => {
              const meta = ROLE_META[entry.role] ?? ROLE_META.participant;
              return (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-glass-100/5 border border-glass-200/10 flex-wrap"
                >
                  <span className="text-sm text-white truncate">
                    {entry.event_title || entry.event?.title || entry.event_id}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border', meta.className)}>
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-glass-500">
                      {format(new Date(entry.added_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Event participants (guide §1.4): players who took part — winners, runners-up,
 * helpers. Adding the same player again upserts their role/notes.
 */
export const EventParticipantsSection = ({
  eventId,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventParticipantsSectionProps) => {
  const [participants, setParticipants] = useState<EventParticipant[]>([]);
  const [rewardUuid, setRewardUuid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newPlayer, setNewPlayer] = useState<PlayerSelection | null>(null);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newRole, setNewRole] = useState<EventParticipantRole>('participant');
  const [newNotes, setNewNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const [historyUuid, setHistoryUuid] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await eventTeamApiService.getEventParticipants(eventId);
      setParticipants(data);
      usernameCacheService.prefetchUsernames(data.map((p) => p.user_uuid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const handleAdd = async () => {
    if (!newPlayer) return;
    setAdding(true);
    try {
      await eventTeamApiService.addParticipant(eventId, {
        user_uuid: newPlayer.uuid,
        display_name: newDisplayName.trim() || newPlayer.name || null,
        role: newRole,
        notes: newNotes.trim() || null,
      });
      setNewPlayer(null);
      setNewDisplayName('');
      setNewRole('participant');
      setNewNotes('');
      setShowAdd(false);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add participant');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (participant: EventParticipant) => {
    if (!confirm('Remove this participant?')) return;
    setBusyUuid(participant.user_uuid);
    try {
      await eventTeamApiService.removeParticipant(eventId, participant.user_uuid);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove participant');
    } finally {
      setBusyUuid(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-6 h-6 animate-spin text-accent-400" />
      </div>
    );
  }

  const winners = participants.filter((p) => p.role === 'winner');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-400" />
          Participants ({participants.length})
          {winners.length > 0 && (
            <span className="inline-flex items-center gap-1 text-yellow-300 text-xs">
              <Trophy className="w-3 h-3" /> {winners.length} winner{winners.length === 1 ? '' : 's'}
            </span>
          )}
        </h3>
        {perms.canLead && (
          <button onClick={() => setShowAdd(!showAdd)} className="glass-button text-xs inline-flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Add participant
          </button>
        )}
      </div>

      {error && (
        <div className="glass-card p-3 border-l-4 border-red-400 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {showAdd && (
        <div className="glass-card p-3 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <PlayerSearchField value={newPlayer} onChange={setNewPlayer} autoFocus />
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder={newPlayer?.name ? `Display name (default: ${newPlayer.name})` : 'Display name (optional)'}
              className="glass-input text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as EventParticipantRole)}
              className="glass-input text-sm"
            >
              {EVENT_PARTICIPANT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_META[r].label}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="Notes (optional)"
              className="glass-input text-sm flex-1 min-w-[160px]"
            />
            <button onClick={() => setShowAdd(false)} className="glass-button-secondary text-xs">
              Cancel
            </button>
            <button
              onClick={() => void handleAdd()}
              disabled={adding || !newPlayer}
              className="glass-button text-xs inline-flex items-center gap-1.5"
            >
              {adding ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add
            </button>
          </div>
        </div>
      )}

      {participants.length === 0 && !error ? (
        <p className="text-glass-400 text-sm py-4 text-center">No participants recorded.</p>
      ) : (
        <div className="space-y-1.5">
          {participants.map((participant) => {
            const meta = ROLE_META[participant.role] ?? ROLE_META.participant;
            const busy = busyUuid === participant.user_uuid;
            return (
              <div
                key={participant.id}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-glass-100/5 border border-glass-200/10 flex-wrap"
              >
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <PlayerProfile
                    uuid={participant.user_uuid}
                    name={participant.display_name ?? undefined}
                    showUuid={false}
                    inline
                  />
                  <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border', meta.className)}>
                    {meta.label}
                  </span>
                  {participant.notes && (
                    <span className="text-[11px] text-glass-500 italic truncate max-w-[280px]" title={participant.notes}>
                      {participant.notes}
                    </span>
                  )}
                  <span className="text-[11px] text-glass-500 inline-flex items-center gap-1">
                    added {formatDistanceToNow(new Date(participant.added_at), { addSuffix: true })} by{' '}
                    <CreatorProfile creatorId={participant.added_by} inline />
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {perms.canPoints && <button type="button" className="glass-button-secondary min-h-[44px]" onClick={() => setRewardUuid(participant.user_uuid)}><Trophy className="w-4 h-4" />Award points</button>}
                  <button
                    onClick={() => setHistoryUuid(participant.user_uuid)}
                    className="glass-button-secondary !p-1.5"
                    title="View this player's event participation history"
                  >
                    <History className="w-3.5 h-3.5" />
                  </button>
                  {perms.canLead && (
                    <button
                      onClick={() => void handleRemove(participant)}
                      disabled={busy}
                      className="glass-button-secondary !p-1.5 text-red-300"
                      title="Remove participant"
                    >
                      {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {historyUuid && <ParticipantHistoryModal uuid={historyUuid} onClose={() => setHistoryUuid(null)} />}
      {perms.canPoints && <AwardPointsModal isOpen={rewardUuid !== null} presetMemberUuid={rewardUuid ?? undefined} presetEventId={eventId} onClose={() => setRewardUuid(null)} onSuccess={() => onChanged?.()} />}
    </div>
  );
};
